import { View } from '../components/orderhistory/types';
import { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import { FrontendRestaurant } from '../services/restaurant/restaurantService';
import { FrontendRestaurantRevenueSource } from '../services/restaurant/restaurantRevenueSourceService';
import { filterRevenueByDateRange } from './dateRangeFilters';
import { getPreviousPeriodRange, calculatePercentageChange } from './kpiChangeCalculations';
import {
  buildRevenueSourceToRestaurantMap,
  groupRevenueByRestaurant,
} from './allRestaurantsDashboardCalculations';
import { calculateTotalRevenue } from './revenueHistoryCalculations';

export interface RestaurantPeriodDelta {
  restaurantId: string;
  restaurantName: string;
  current: number;
  previous: number;
  changeValue: number; // absolute change (current - previous)
  changePercent: number; // percentage change, 0 when previous is 0
}

/**
 * Compute per-restaurant current-vs-previous-period revenue deltas for the
 * all-restaurants Revenue History view.
 *
 * Reuses the same building blocks as the rest of the all-restaurants flow:
 *  - `filterRevenueByDateRange` for period scoping (current + previous)
 *  - `getPreviousPeriodRange` for the previous-period math (day/week/month; 'all' → null)
 *  - the per-restaurant grouping from `allRestaurantsDashboardCalculations`
 *  - `calculateTotalRevenue` for per-restaurant totals
 *
 * The result is sorted by `changePercent` descending, so the head of the list is
 * the "Top Riser" (largest positive %) and the tail is the "Biggest Faller"
 * (largest negative %).
 *
 * @param allRevenue Full (unfiltered) all-restaurants revenue list.
 * @param view Current time view ('day' | 'week' | 'month' | 'all' | ...).
 * @param startDate Start of the current period.
 * @param endDate End of the current period.
 * @param restaurants Restaurants (for id → name resolution).
 * @param revenueSourcesMap Per-restaurant revenue sources (for source → restaurant lookup).
 * @param showExVat Whether totals use ex-VAT amounts.
 */
export function calculateRestaurantPeriodDeltas(
  allRevenue: RevenueHistory[],
  view: View,
  startDate: Date,
  endDate: Date,
  restaurants: FrontendRestaurant[],
  revenueSourcesMap: Record<string, FrontendRestaurantRevenueSource[]>,
  showExVat: boolean
): RestaurantPeriodDelta[] {
  const revSourceToRestaurant = buildRevenueSourceToRestaurantMap(revenueSourcesMap);
  const restaurantNameMap = new Map(restaurants.map(r => [r.id, r.name]));

  // Current period.
  const currentRevenue = filterRevenueByDateRange(allRevenue, startDate, endDate);
  const currentByRestaurant = groupRevenueByRestaurant(currentRevenue, revSourceToRestaurant);

  // Previous period (null for 'all' view → no comparison, all previous = 0).
  const previousRange = getPreviousPeriodRange(view, startDate, endDate);
  const previousRevenue = previousRange
    ? filterRevenueByDateRange(
        allRevenue,
        previousRange.previousStartDate,
        previousRange.previousEndDate
      )
    : [];
  const previousByRestaurant = groupRevenueByRestaurant(previousRevenue, revSourceToRestaurant);

  // Every restaurant that has data in either period.
  const restaurantIds = Array.from(new Set<string>([
    ...Object.keys(currentByRestaurant),
    ...Object.keys(previousByRestaurant),
  ]));

  const deltas: RestaurantPeriodDelta[] = [];

  for (const rId of restaurantIds) {
    if (rId === 'unknown') continue;

    const current = calculateTotalRevenue(currentByRestaurant[rId] || [], showExVat);
    const previous = calculateTotalRevenue(previousByRestaurant[rId] || [], showExVat);

    deltas.push({
      restaurantId: rId,
      restaurantName: restaurantNameMap.get(rId) || rId,
      current,
      previous,
      changeValue: current - previous,
      changePercent: calculatePercentageChange(current, previous),
    });
  }

  // Largest positive % first (Top Riser), largest negative % last (Biggest Faller).
  deltas.sort((a, b) => b.changePercent - a.changePercent);
  return deltas;
}
